import moment from 'moment';
// import capitalize from 'src/utils/capitalize';
import { hydrateMobX } from './helpMethods';

const dollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',

  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

export class HydratedStore implements IHydratedStore {
  STORAGE_ID: string = 'd4urx';
  hydrate = async () => this.STORAGE_ID && await hydrateMobX(this.STORAGE_ID, this);

  constructor(storageId: string) {
    this.STORAGE_ID = storageId;
  }
}

export class FormattedGridData {
  data: any;
  formatFunction: Function;

  constructor(data: any, formatFunction: (data: any) => string) {
    this.data = data;
    this.formatFunction = formatFunction;
  }

  toString() {
    return this.formatFunction(this.data);
  }
}

export class UserDetails {
  user_id: number;
  // user_role: UserRole;
  email: string;
  display_name: string;
  active: boolean;
  // dtg_user: boolean;
  activation_partner_id: number[];
  user_role_list: UserRole[];
  user_role: string;

  constructor(data: any) {
    this.user_id = data.user_id;
    // this.user_role = data.user_role;
    this.email = data.email;
    this.display_name = data.display_name;
    this.active = data.active;
    // this.dtg_user = data.dtg_user;
    this.activation_partner_id = String(data.activation_partner_id || '').split(',').map(Number);
    this.user_role_list = String(data.user_role_list || '').split(',').map(role => role as UserRole);
    this.user_role = data.user_role;
  }

  hasPermission(permission: string) {
    return true;
    // console.log('hasPermission', this.user_role_list.includes(permission as UserRole), permission, this.user_role_list);
    // return this.user_role_list.includes(permission as UserRole);
  }
}

export class User {
  credentials: UserDetails;
  token: string;

  constructor(user: any) {
    this.credentials = new UserDetails(user.credentials);
    this.token = user.token;
  }
}

export class Dealer {
  dealer_id: number;
  dealer_name: string;
  parts_code: string;
  region: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  primary_contact: string;
  contact_email: string;
  contact_phone: string;
  secondary_contact: string;
  secondary_contact_email: string;
  secondary_contact_phone: string;
  tertiary_contact: string;
  tertiary_contact_email: string;
  tertiary_contact_phone: string;
  first_time_dealer: boolean;
  hispanic: boolean;
  active: boolean;
  attended_training: boolean;
  training_attendee_name: string;
  training_date: Date;
  approved_dealers: string;
  created_timestamp: Date;
  updated_timestamp: Date;
  ffs_event_id?: string;
  training_completed?: Date;
  start_date?: Date;
  dealer_notes?: DealerNote[];
  waitlisted?: string;
  invited_off_waitlist?: string;
  event_requested?: boolean;
  event_requested_at?: Date;
  status?: string;
  season_id?: number;

  constructor(dealer: any) {
    this.dealer_id = dealer.dealer_id;
    this.dealer_name = dealer.dealer_name;
    this.parts_code = dealer.parts_code;
    this.region = dealer.region;
    this.address = dealer.address;
    this.address2 = dealer.address2;
    this.city = dealer.city;
    this.state = dealer.state;
    this.zip = dealer.zip;
    this.phone = dealer.phone;
    this.primary_contact = dealer.primary_contact;
    this.contact_email = dealer.contact_email;
    this.contact_phone = dealer.contact_phone;
    this.secondary_contact = dealer.secondary_contact;
    this.secondary_contact_email = dealer.secondary_contact_email;
    this.secondary_contact_phone = dealer.secondary_contact_phone;
    this.tertiary_contact = dealer.tertiary_contact;
    this.tertiary_contact_email = dealer.tertiary_contact_email;
    this.tertiary_contact_phone = dealer.tertiary_contact_phone;
    this.first_time_dealer = dealer.first_time_dealer;
    this.hispanic = dealer.hispanic;
    this.active = dealer.active === 1; // convert to boolean
    this.attended_training = dealer.attended_training;
    this.training_attendee_name = dealer.training_attendee_name;
    this.training_date = moment(dealer.training_date).toDate();
    this.approved_dealers = dealer.approved_dealers;
    this.created_timestamp = moment(dealer.created_timestamp).toDate(); // convert to date
    this.updated_timestamp = moment(dealer.updated_timestamp).toDate(); // convert to date
    this.ffs_event_id = dealer.ffs_event_id;
    this.training_completed = dealer.training_completed ? moment(dealer.training_completed).toDate() : undefined; // convert to date
    this.start_date = dealer.start_date ? moment(dealer.start_date).toDate() : undefined; // convert to date
    
    // Parse dealer notes similar to how we handle outreach notes
    try {
      if (dealer.dealer_notes && typeof dealer.dealer_notes === 'string') {
        // Clean up the string and check if it's valid JSON
        const cleanedNotes = dealer.dealer_notes.trim();
        if (cleanedNotes === '' || cleanedNotes === 'null' || cleanedNotes === 'undefined' || cleanedNotes === '[]') {
          this.dealer_notes = [];
        } else if (cleanedNotes.startsWith('[') || cleanedNotes.startsWith('{')) {
          this.dealer_notes = JSON.parse(cleanedNotes);
        } else {
          // If it's not JSON format, treat as empty and log it
          console.warn('dealer_notes not in JSON format for dealer', dealer.dealer_id, ':', cleanedNotes);
          this.dealer_notes = [];
        }
      } else if (Array.isArray(dealer.dealer_notes)) {
        this.dealer_notes = dealer.dealer_notes;
      } else {
        this.dealer_notes = [];
      }
    } catch (e) {
      console.error('Failed to parse dealer_notes for dealer', dealer.dealer_id, ':', {
        raw: dealer.dealer_notes,
        type: typeof dealer.dealer_notes,
        error: e.message
      });
      this.dealer_notes = [];
    }
    
    this.waitlisted = dealer.waitlisted;
    this.invited_off_waitlist = dealer.invited_off_waitlist;
    this.event_requested = dealer.event_requested;
    this.event_requested_at = dealer.event_requested_at;
    this.status = dealer.status;
    this.season_id = dealer.season_id;
  }

  public get activeGridCell(): any {
    return this.active ? 'Active' : 'Inactive';
  }

  public get firstTimeDealerGridCell(): any {
    return this.first_time_dealer ? 'Yes' : 'No';
  }

  public get hispanicReadable(): any {
    return this.hispanic ? 'Yes' : 'No';
  }
}

const eventTypes = [
  { text: 'No test-drive or walkaround', value: 'basic', export: 2 },
  { text: 'Test-Drive', value: 'test_drive', export: 1 },
  { text: 'Walkaround', value: 'walkaround', export: 3 },
];

export class D4URxEvent {
  event_id?: number;
  event_request_id?: number;
  event_name?: string;
  event_series_id?: number;
  bonus_event?: boolean;
  season_id: number;
  season_end_date?: string; // YYYY-MM-DD, the latest date the event can move to
  dealer_id: number;
  dealer_name: string;
  dealer_notes?: DealerNote[];
  dealer_notes_raw?: string; // Raw string for export
  first_time_dealer?: boolean;
  hispanic?: boolean;
  /** From history / event-by-season API (0/1); also drives `hispanic` when `hispanic` is absent */
  hispanic_dealer?: boolean | number;
  gets_spanish_kit?: boolean;
  parts_code: string;
  pa_code?: string;
  region?: string;
  market_area?: string;
  start_date?: Date;
  end_date?: Date;
  start_time?: Date;
  end_time?: Date;
  time_zone?: string;
  ffs_event_id?: number;
  drive_event_id?: number;
  training_completed?: Date;
  training_completed_by?: string;
  vincent_code?: string;
  vincent_value?: number;
  pre_reg_hash?: string;
  cancelled?: boolean;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
  cancellation_notes?: string | null;
  ended?: boolean;
  check_data_processed?: Date;
  dealer_reminder_email_sent?: Date;
  charity_partner_id?: number;
  charity_partner?: string;
  charity_address_1?: string;
  charity_address_2?: string;
  charity_city?: string;
  charity_state?: string;
  charity_postal_code?: string;
  event_location?: string;
  event_location_name?: string;
  address?: string;
  address_2?: string;
  city?: string;
  zip_code?: string;
  state?: string;
  digital_event_code?: string;
  logged_in?: Date;
  spreadsheet_data?: string;
  invalid_address?: boolean;
  edit_log?: string;
  decline_reason?: string | null;
  status?: D4URxEventStatus;
  training_status?: D4URxTrainingStatus;
  created_by_user_id?: number;
  created_timestamp: Date;
  updated_timestamp: Date;
  location_contact?: string;
  location_phone?: string;
  location_email?: string;
  funding_purpose?: string;
  funding_purpose_category?: string;
  attendance?: number;
  rejected?: boolean;
  check_location?: string;
  check_payable?: string;
  check_attention?: string;
  check_address?: string;
  check_address_2?: string;
  check_city?: string;
  check_state?: string;
  check_zip_code?: string;
  display_name?: string;
  custom_data?: string;
  eligibility_custom_data?: string;
  event_custom_data?: string;
  ffs_ford_campaign?: string;
  ffs_ford_sequence?: string;
  check_amount?: number;
  secondary_charity: string;
  attn_contact_name?: string;
  attn_contact_email?: string;
  attn_contact_phone?: string;
  dealer_contact_email?: string;
  dealer_contact_name?: string;
  dealer_contact_phone?: string;
  dealer_email?: string;
  dealer_secondary_email?: string;
  dealer_tertiary_email?: string;
  kit_tracking_number?: string;
  secondary_tracking_number?: string;
  shipping_location?: string;
  valid_post_count?: number;
  bonus_valid_count?: number;
  total_pre_drives?: number;
  total_post_drives?: number;
  pre_registrant_count?: number;
  total_bonus_drives?: number;
  sales_code?: string;
  survey_count?: number;
  dealer_address?: string;
  dealer_city?: string;
  dealer_state?: string;
  dealer_zip?: string;
  organization_address?: string;
  organization_city?: string;
  organization_state?: string;
  organization_zip?: string;
  primary_contact?: string;
  active?: boolean;
  nat_charity?: string;
  organization_type?: 'school' | 'community_org' | null; // D4URX-2750, set on approval
  organization_subcategory?: string | null; // D4URX-2760, set on approval
  fbt_partner?: string | null; // D4URX-2760, set on approval; separate from the dealer's nat_charity
  outreach_done?: Date;
  outreach_notes?: OutreachNote[];
  dealer_feedback?: DealerFeedback[];
  outreach_notes_raw?: string; // Raw string for export


  constructor(event: any) {
    this.event_id = event.event_id;
    this.event_request_id = event.event_request_id;
    this.event_name = event.event_name;
    this.event_series_id = event.event_series_id;
    this.bonus_event = event.bonus_event;
    this.season_id = event.season_id;
    this.season_end_date = event.season_end_date;
    this.dealer_id = event.dealer_id;
    this.dealer_name = event.dealer_name;
    this.dealer_notes_raw = event.dealer_notes; // Raw string for export
    
    try {
      if (event.dealer_notes && typeof event.dealer_notes === 'string') {
        // Clean up the string and check if it's valid JSON
        const cleanedNotes = event.dealer_notes.trim();
        if (cleanedNotes === '' || cleanedNotes === 'null' || cleanedNotes === 'undefined' || cleanedNotes === '[]') {
          this.dealer_notes = [];
        } else if (cleanedNotes.startsWith('[') || cleanedNotes.startsWith('{')) {
          this.dealer_notes = JSON.parse(cleanedNotes);
        } else {
          // If it's not JSON format, treat as empty and log it
          console.warn('dealer_notes not in JSON format for event', event.event_id, ':', cleanedNotes);
          this.dealer_notes = [];
        }
      } else if (Array.isArray(event.dealer_notes)) {
        this.dealer_notes = event.dealer_notes;
      } else {
        this.dealer_notes = [];
      }
    } catch (e) {
      console.error('Failed to parse dealer_notes for event', event.event_id, ':', {
        raw: event.dealer_notes,
        type: typeof event.dealer_notes,
        error: e.message
      });
      this.dealer_notes = [];
    }
    this.first_time_dealer = event.first_time_dealer;
    this.hispanic_dealer = event.hispanic_dealer;
    const hispanicSource =
      event.hispanic !== undefined && event.hispanic !== null ? event.hispanic : event.hispanic_dealer;
    this.hispanic =
      hispanicSource === true || hispanicSource === 1 || hispanicSource === '1';
    this.gets_spanish_kit = event.gets_spanish_kit;
    this.parts_code = event.parts_code;
    this.pa_code = event.pa_code;
    this.region = event.region;
    this.market_area = event.market_area;
    this.start_date = event.start_date ? moment(event.start_date).toDate() : undefined;
    this.end_date = event.end_date ? moment(event.end_date).toDate() : undefined;
    this.start_time = event.start_time ? moment(event.start_time, 'HH:mm:ss').toDate() : undefined;
    this.end_time = event.end_time ? moment(event.end_time, 'HH:mm:ss').toDate() : undefined;
    this.time_zone = event.time_zone;
    this.ffs_event_id = event.ffs_event_id;
    this.drive_event_id = event.drive_event_id;
    this.training_completed = event.training_completed ? moment(event.training_completed).toDate() : undefined;
    this.training_completed_by = event.training_completed_by;
    this.vincent_code = event.vincent_code;
    this.vincent_value = event.vincent_value;
    this.pre_reg_hash = event.pre_reg_hash;
    this.cancelled = event.cancelled === '1' ? true : false;
    // The API sends `cancelled` as a timestamp, so the flag above is always false. Kept as-is
    // because Events.tsx relies on it; use cancelled_at to know whether an event is cancelled.
    this.cancelled_at = event.cancelled || null;
    this.cancellation_reason = event.cancellation_reason;
    this.cancellation_notes = event.cancellation_notes;
    this.ended = event.ended;
    this.check_data_processed = event.check_data_processed ? moment(event.check_data_processed).toDate() : undefined;
    this.dealer_reminder_email_sent = event.dealer_reminder_email_sent ? moment(event.dealer_reminder_email_sent).toDate() : undefined;
    this.charity_partner_id = event.charity_partner_id;
    this.charity_partner = event.charity_partner;
    
    // New charity address fields from backend
    this.charity_address_1 = event.charity_address_1;
    this.charity_address_2 = event.charity_address_2;
    this.charity_city = event.charity_city;
    this.charity_state = event.charity_state;
    this.charity_postal_code = event.charity_postal_code;
    
    // New dealer address fields from backend
    this.dealer_city = event.dealer_city;
    this.dealer_state = event.dealer_state;
    this.dealer_zip = event.dealer_zip;
    
    this.event_location = event.event_location;
    this.event_location_name = event.event_location_name;
    this.address = event.address;
    this.address_2 = event.address_2;
    this.city = event.city;
    this.zip_code = event.zip_code || '';
    this.state = event.state;
    this.digital_event_code = event.digital_event_code;
    this.logged_in = event.logged_in ? moment(event.logged_in).toDate() : undefined;
    this.spreadsheet_data = event.spreadsheet_data;
    this.invalid_address = event.invalid_address;
    this.edit_log = event.edit_log;
    this.decline_reason = event.decline_reason;
    this.status = event.status;
    this.training_status = event.training_status;
    this.created_by_user_id = event.created_by_user_id;
    this.created_timestamp = moment(event.created_timestamp).toDate();
    this.updated_timestamp = moment(event.updated_timestamp).toDate();
    this.location_contact = event.location_contact;
    this.location_phone = event.location_phone;
    this.location_email = event.location_email;
    this.funding_purpose = event.funding_purpose;
    this.funding_purpose_category = event.funding_purpose_category;
    this.attendance = event.attendance;
    this.rejected = event.rejected;
    this.check_location = event.check_location;
    this.check_payable = event.check_payable;
    this.check_attention = event.check_attention;
    this.check_address = event.check_address;
    this.check_address_2 = event.check_address_2;
    this.check_city = event.check_city;
    this.check_state = event.check_state;
    this.check_zip_code = event.check_zip_code || '';
    this.display_name = event.display_name;
    this.custom_data = event.custom_data;
    this.eligibility_custom_data = event.eligibility_custom_data;
    this.event_custom_data = event.event_custom_data;
    this.ffs_ford_campaign = event.ffs_ford_campaign;
    this.ffs_ford_sequence = event.ffs_ford_sequence;
    this.check_amount = event.check_amount;
    this.secondary_charity = event.secondary_charity;
    this.attn_contact_name= event.attn_contact_name;
    this.attn_contact_email = event.attn_contact_email;
    this.attn_contact_phone = event.attn_contact_phone;
    this.dealer_contact_email = event.dealer_contact_email;
    this.dealer_contact_name = event.dealer_contact_name;
    this.dealer_contact_phone = event.dealer_contact_phone;
    this.dealer_email = event.dealer_email;
    this.dealer_secondary_email = event.dealer_secondary_email;
    this.dealer_tertiary_email = event.dealer_tertiary_email;
    this.kit_tracking_number = event.kit_tracking_number;
    this.secondary_tracking_number = event.secondary_tracking_number;
    this.shipping_location = event.shipping_location;
    this.valid_post_count = event.valid_post_count;
    this.bonus_valid_count = event.bonus_valid_count;
    this.total_pre_drives = event.total_pre_drives;
    this.total_post_drives = event.total_post_drives;
    this.pre_registrant_count = event.pre_registrant_count;
    this.total_bonus_drives = event.total_bonus_drives;
    this.sales_code = event.sales_code;
    this.survey_count = event.survey_count;
    this.dealer_address = event.dealer_address;
    this.dealer_city = event.dealer_city;
    this.dealer_state = event.dealer_state;
    this.dealer_zip = event.dealer_zip;
    this.organization_address = event.organization_address;
    this.organization_city = event.organization_city;
    this.organization_state = event.organization_state;
    this.organization_zip = event.organization_zip;
    this.primary_contact = event.primary_contact;
    this.active = event.active;
    this.nat_charity = event.nat_charity;
    this.organization_type = event.organization_type || null;
    this.organization_subcategory = event.organization_subcategory || null;
    this.fbt_partner = event.fbt_partner || null;
    this.outreach_done = event.outreach_done;
    this.outreach_notes_raw = event.outreach_notes; // Raw string for export
    try {
      if (event.outreach_notes && typeof event.outreach_notes === 'string') {
        this.outreach_notes = JSON.parse(event.outreach_notes);
      } else if (Array.isArray(event.outreach_notes)) {
        this.outreach_notes = event.outreach_notes;
      } else {
        this.outreach_notes = [];
      }
    } catch (e) {
      console.warn('Failed to parse outreach_notes for event', event.event_id, ':', event.outreach_notes, e);
      this.outreach_notes = [];
    }
    try {
      const feedback = typeof event.dealer_feedback === 'string' ? JSON.parse(event.dealer_feedback) : event.dealer_feedback;
      this.dealer_feedback = Array.isArray(feedback) ? feedback : [];
    } catch (e) {
      console.warn('Failed to parse dealer_feedback for event', event.event_id, e);
      this.dealer_feedback = [];
    }
  }

  public get start_date_export(): string {
    return moment(this.start_date).format('M/D/YY')
  }

  public get end_date_export(): string {
    return moment(this.end_date).format('M/D/YY')
  }

  public get audience_type(): string {
    return 'G';
  }

  public get category(): string {
    return 'Deal';
  }

  public get language_option(): string {
    return 'N';
  }

  public get indoor_outdoor(): string {
    return '';
  }

  public get firstTimeDealerGridCell(): any {
    return this.first_time_dealer ? 'Yes' : 'No';
  }
  public get hispanicReadable(): any {
    return this.hispanic ? 'Yes' : 'No';
  }
  public get getsSpanishKit(): any {
    return this.gets_spanish_kit ? 'Yes' : 'No';
  }

  public get test_drive(): number {
    // 1 = Yes, 2 = No, 3 = Walkaround
    const { centuryParty } = JSON.parse(this.event_custom_data || '{}');
    if (centuryParty) {
      return eventTypes.find(x => x.value === centuryParty)?.export || 2;
    } else {
      return 2;
    }
  }

  public get test_drive_full(): string {
    const { centuryParty } = JSON.parse(this.event_custom_data || '{}');
    if (centuryParty) {
      const thisType = eventTypes.find(x => x.value === centuryParty)?.export;
      return thisType === 1 ? 'Test Drive Event' : 'Non-Test Drive Event';
    } else {
      return 'Non-Test Drive Event';
    }
  }

  public get regional(): string {
    return 'Nat';
  }

  public get lincoln_event_partner(): number {
    return 2;
  }

  public get audience_type_full(): string {
    return 'General';
  }

  public get category_long(): string {
    return 'Dealer Support';
  }

  public get regional_full(): string {
    return 'National';
  }

  public get requested_month(): string | undefined {
    const { requested_month } = JSON.parse(this.eligibility_custom_data || '{}');
    return requested_month;
  }

  public get requested_date(): Date | undefined {
    // const { requested_date } = JSON.parse(this.eligibility_custom_data || '{}');
    // return requested_date ? moment(requested_date).toDate() : undefined;
    return this.start_date;
  }

  public get requested_date_formatted(): string | undefined {
    // const { requested_date } = JSON.parse(this.eligibility_custom_data || '{}');
    // return requested_date ? moment(requested_date).format('dddd, MMMM Do') : undefined;
    return this.start_date ? moment(this.start_date).format('dddd, MMMM Do') : undefined;
  }

  public get event_type(): any {
    const { centuryParty } = JSON.parse(this.event_custom_data || '{}');
    if (centuryParty) {
      return eventTypes.find(x => x.value === centuryParty)
    } else {
      return undefined;
    }
  }

  public get event_type_display(): string | undefined {
    const { centuryParty } = JSON.parse(this.event_custom_data || '{}');
    if (centuryParty) {
      const type = eventTypes.find(x => x.value === centuryParty);
      return type?.text;
    } else {
      return undefined;
    }
  }

  public get event_location_type(): any {
    const { event_location_type } = JSON.parse(this.event_custom_data || '{}');
    return event_location_type;
  }

  public get kit_location(): any {
    const { kit_location } = JSON.parse(this.event_custom_data || '{}');
    return kit_location;
  }

  public get additional_schools(): any {
    const { additional_schools } = JSON.parse(this.event_custom_data || '{}');
    return additional_schools;
  }

  public get json(): string {
    const event = {
      ...this,
      // override values
      start_date: this.start_date ? moment(this.start_date).format('YYYY-MM-DD') : null,
      end_date: this.end_date ? moment(this.end_date).format('YYYY-MM-DD') : null,
      start_time: this.start_time ? moment(this.start_time).format('HH:mm:00') : null,
      end_time: this.end_time ? moment(this.end_time).format('HH:mm:00') : null,
    };

    return JSON.stringify(event);
  }

  // public get start_date_string(): string|undefined {
  //   return this.start_date ? moment(this.start_date).format('YYYY-MM-DD') : undefined;
  // }

  // public get start_time_string(): string|undefined {
  //   return this.start_time ? moment(this.start_time).format('HH:mm:ss') : undefined;
  // }

  // public get end_date_string(): string|undefined {
  //   return this.end_date ? moment(this.end_date).format('YYYY-MM-DD') : undefined;
  // }

  // public get end_time_string(): string|undefined {
  //   return this.end_time ? moment(this.end_time).format('HH:mm:ss') : undefined;
  // }

  public get status_pretty(): string | undefined {
    // return capitalize(this.status);
    const words = this.status?.split(" ");
    if (!words) {
      return '';
    }
    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }
    return words.join(" ");
  }

  public get training_status_pretty(): string | undefined {
    const words = this.training_status?.split(" ");
    if (!words) {
      return '';
    }
    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }
    return words.join(" ");
  }

  public get formatted_custom_data(): any {
    // if this.event_custom_data is a string, parse it into an object and return it. otherwise, return an empty object  
    return this.event_custom_data ? JSON.parse(this.event_custom_data) : {};
  }

  public get check_amount_pretty(): string | undefined {
    if (this.check_amount) {
      return dollarFormatter.format(this.check_amount);
    }
    return '';
  }

  public get sort_status(): number {
    switch (this.status) {
      case 'new': // dealer has submitted charity eligibility
        return 0;
      case 'eligible': // PHQ has approved charity eligibility
        return 1;
      case 'planning': // dealer is planning event
        return 2;
      case 'revise': // dealer needs to revise event plan
        return 3;
      case 'review': // PHQ needs to review event plan
        return 3.25;
      case 'approved': // event plan approved, event has not happened yet
        return 4;
      case 'event ending': // event has ended, need to approve donation
        return 5;
      case 'event ended': // event has ended, need to approve donation
        return 5.5;
      case 'donation requested': // donation requested by PHQ
        return 6;
      case 'donation approved': // donation approved by Adrienne, dealer needs to submit reimbursement plan
        return 7;
      case 'reimbursement submitted': // reimbursement requested by dealer, needs to be approved by PHQ
        return 8;
      case 'reimbursement requested': // reimbursement requested by PHQ, needs to be approved by Adrienne
        return 9;
      case 'reimbursement approved': // reimbursement approved by Adrienne
        return 10;
      case 'ineligible': // charity was not eligible
        return 100;
      case 'rejected': // event is rejected
        return 101;
      case 'cancelled': // event is cancelled
        return 102;
      default:
        return 999;
    }
  }

  public get sort_date(): Date {
    const { requested_date, requested_month } = JSON.parse(this.eligibility_custom_data || '{}');
    let dateValue: Date;
    if (this.start_date) {
      dateValue = this.start_date;
    } else if (requested_date) {
      dateValue = moment(requested_date).toDate();
    } else {
      // TODO: hard-coded to current year, obviously won't work all the time
      dateValue = moment(`${requested_month} 1 ${moment().year()}`).toDate();
    }

    return dateValue;
  }

  public get outreach_status_filter(): string {
    const hasNotes = this.outreach_notes && 
      (Array.isArray(this.outreach_notes) ? this.outreach_notes.length > 0 : 
       (typeof this.outreach_notes === 'string' && this.outreach_notes !== '[]' && this.outreach_notes !== ''));
    
    const isDone = this.outreach_done !== null;
    
    if (isDone && hasNotes) {
      return 'Done with Notes';
    } else if (isDone && !hasNotes) {
      return 'Done without Notes';
    } else if (!isDone && hasNotes) {
      return 'Not Done with Notes';
    } else {
      return 'Not Done without Notes';
    }
  }
}

interface OutreachNote {
  note: string;
  created_at: string;
  created_by: string;
}

// D4URX-2752: per-event dealer feedback, stored on event_requests.dealer_feedback
export interface DealerFeedback {
  id: string;
  note: string;
  contact: string | null;
  created_at: string; // ISO
  created_by: string;
  created_by_name: string | null;
  updated_at?: string;
  updated_by?: string;
}

export class QueuedCheck {
  check_queue_id: number;
  status: string;
  ffs_event_id?: number;
  check_type: string;
  check_amount: number;
  check_approved?: Date;
  start_date?: Date;
  event_name?: string;
  event_id?: number;
  custom_data?: any;
  dealer_name?: string;
  event_summary?: string;
  check_payable?: string;
  dealer_id?: number;
  season_id?: number;
  event_series_id?: number;
  created_timestamp: Date;
  updated_timestamp: Date;
  tracking_number: string;
  check_email_sent?: Date;

  constructor(check: any) {
    this.custom_data = JSON.parse(check.custom_data || '{}');

    this.check_queue_id = check.check_queue_id;
    this.ffs_event_id = check.ffs_event_id;
    this.check_approved = check.check_approved ? moment.utc(check.check_approved).toDate() : undefined;
    this.check_type = this.custom_data.check_type || check.check_type;
    this.check_amount = check.check_amount;
    this.start_date = this.custom_data.event_date || check.start_date;
    this.event_name = this.custom_data.event_name || check.event_name;
    this.event_summary = JSON.parse(check.custom_data || '{}').event_summary;
    this.dealer_name = this.custom_data.dealer_name || check.dealer_name;
    this.check_payable = check.check_payable;
    this.dealer_id = check.dealer_id;
    this.season_id = check.season_id;
    this.event_series_id = check.event_series_id;
    this.event_id = check.event_id;
    // DB stores naive datetimes as UTC (e.g. 17:24 UTC → 1:24 PM Eastern same calendar day in EDT)
    this.created_timestamp = moment.utc(check.created_timestamp).toDate();
    this.updated_timestamp = moment.utc(check.updated_timestamp).toDate();
    this.status = this.check_approved ? 'Approved' : 'Pending';
    this.tracking_number = check.tracking_number;
    this.check_email_sent = check.check_email_sent;
  }

  public get memo(): string {
    return this.custom_data?.memo || '';
  }

  public get sort_status(): number {
    switch (this.status) {
      case 'Pending':
        return 0;
      case 'Approved':
        return 1;
      default:
        return 999;
    }
  }

  public get check_type_pretty(): string | undefined {
    // return capitalize(this.check_type);
    const words = this.check_type?.split("_");
    if (!this.check_type) {
      return '';
    }

    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }
    return words.join(" ");
  }

  public get check_amount_pretty(): string | undefined {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',

      // These options are needed to round to whole numbers if that's what you want.
      //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
      //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
    });

    return formatter.format(this.check_amount);
  }

  // public get sort_status():number {
  //   switch (this.status) {
  //       case 'new': // dealer has submitted charity eligibility
  //         return 0;
  //       case 'eligible': // PHQ has approved charity eligibility
  //         return 1;
  //       case 'planning': // dealer is planning event
  //         return 2;
  //       case 'revise': // dealer needs to revise event plan
  //         return 3;
  //       case 'review': // PHQ needs to review event plan
  //         return 3.25;
  //       case 'approved': // event plan approved, event has not happened yet
  //         return 4;
  //       case 'event ending': // event has ended, need to approve donation
  //         return 5;
  //       case 'event ended': // event has ended, need to approve donation
  //         return 5.5;
  //       case 'donation requested': // donation requested by PHQ
  //         return 6;
  //       case 'donation approved': // donation approved by Adrienne, dealer needs to submit reimbursement plan
  //         return 7;
  //       case 'reimbursement submitted': // reimbursement requested by dealer, needs to be approved by PHQ
  //         return 8;
  //       case 'reimbursement requested': // reimbursement requested by PHQ, needs to be approved by Adrienne
  //         return 9;
  //       case 'reimbursement approved': // reimbursement approved by Adrienne
  //         return 10;
  //       case 'ineligible': // charity was not eligible
  //         return 100;
  //       case 'rejected': // event is rejected
  //         return 101;
  //       case 'cancelled': // event is cancelled
  //         return 102;
  //       default:
  //         return 999;
  //   }
  // }
}
